import { AppRegistrationSection } from '../../AppRegistrationSection';
import { Card, Spacer, Text, Title } from 'components';
import { CheckmarkIcon, PhoneCallIcon } from 'components/icons';
import { Frame } from 'components/organisms';
import { frameConfig, frameConfigBasic } from '../../AppConfig';
import { getFetchHeaders } from 'utils/fetchHeaders';
import { getScreenTranslation } from '../../utils/translations';
import { RequestLoadingTemplate } from 'InternalLibrary';
import { routes } from '../../routes';
import { useEffect, useMemo, useState } from 'react';
import { useGetClientApplication } from 'api';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePayment } from '../../hooks/usePayment';
import { useTranslation } from 'react-i18next';
import ActivePayment from 'components/molecules/ActivePayment/ActivePayment';
import layout from 'scss/layout.module.scss';
import styles from './RequestSuccess.module.scss';

export default function RegistrationSuccess() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { fetch: fetchGetApplication } = useGetClientApplication();
  const i18Next = useTranslation();

  const isNoTinkRoute = routes.FirstLoan_Tink_Email_Pending === pathname;
  const [loading, setLoading] = useState(isNoTinkRoute);
  const continueScreen = isNoTinkRoute
    ? routes.FirstLoan_OnlineBank_Pending
    : routes.FirstLoan_Tink_Email_Pending;

  const { activateAutoRepay, showAutoPaymentSection } = usePayment({
    continueScreen,
  });

  useEffect(() => {
    const checkApplicationStatus = async () => {
      const fetchedApplication = await fetchGetApplication({
        headers: getFetchHeaders({
          Accept: 'application/vnd.4finance.web.v1.hal+json',
          'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
        }),
      });
      if (fetchedApplication?.resolution === 'REJECTED') {
        navigate(routes.FirstLoan_Rejected);
      } else {
        setLoading(false);
      }
    };
    if (isNoTinkRoute) checkApplicationStatus();
  }, []);

  const content = getScreenTranslation('registration_success', i18Next);

  const onActiveBtnHandler = showAutoPaymentSection
    ? activateAutoRepay
    : undefined;

  // TODO: Pending to enable the auto-repayment option. For now, it's only available in the screen "LoanApproved"
  const SecondCard = useMemo(
    () =>
      onActiveBtnHandler &&
      content.activateBtn &&
      content.messageSecondCard &&
      content.titleMessageSecondCard && (
        <Spacer marginBottom="30px" marginTop="-1px">
          <ActivePayment
            onActiveBtnHandler={onActiveBtnHandler}
            content={{
              activateBtn: content.activateBtn,
              message: content.messageSecondCard,
              title: content.titleMessageSecondCard,
            }}
            cardClassName={styles['request-success-card']}
            data-test="css-Autorepayment-Card"
          />
        </Spacer>
      ),
    [
      onActiveBtnHandler,
      content.activateBtn,
      content.messageSecondCard,
      content.titleMessageSecondCard,
    ],
  );

  if (loading) {
    return (
      <RequestLoadingTemplate
        {...frameConfigBasic}
        hideLogout
        hideMobileFooter
        content={getScreenTranslation('registration_loading', i18Next)}
        sectionID={AppRegistrationSection.Confirmation}
      />
    );
  }

  return (
    <Frame
      {...frameConfig}
      hideEditButton={true}
      bodyWrapperClassName={layout['frame-x-compact']}
      sectionID={AppRegistrationSection.Summary}
    >
      <div className={layout['width-lg']}>
        <Spacer
          paddingHorizontal={{ s: '20px' }}
          marginBottom={{ default: '50px', s: '35px' }}
        >
          <Title
            icon={<CheckmarkIcon />}
            text={content.title}
            data-test="css-Title"
          />
        </Spacer>
        <Spacer marginBottom={{ default: '20px', s: '0px' }}>
          <Card className={styles['request-success-card']}>
            <Card className={styles['card-message']} intent="success">
              <Spacer
                padding={{ default: '25px 25px 31px', s: '28px 25px 31px' }}
              >
                <div
                  className={styles['title-message']}
                  data-test="css-Info-Container"
                >
                  <PhoneCallIcon />
                  <span>{content.titleMessageGreen}</span>
                </div>
                <Text level={2} text={content.messageGreen} />
              </Spacer>
            </Card>
          </Card>
        </Spacer>
        {onActiveBtnHandler && SecondCard}
      </div>
    </Frame>
  );
}
